import type { ReactNode } from "react";
import { PrismButton, PrismInputText } from "@prism-ui/react";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import "./Projects.css";

interface FilterBarProps {
  availableYears: Array<number | string>;
  availableTags: string[];
  availableLocations: string[];
  selectedYears: Array<number | string>;
  selectedTags: string[];
  selectedLocations: string[];
  search: string;
  lockedYear?: number | string;
  /** Hide the Year facet entirely (e.g. on a single-fair page where year is fixed). */
  hideYear?: boolean;
  /** Filtered result count and grand total, for the "Showing X of Y" summary. */
  resultCount?: number;
  totalCount?: number;
  /** Rendered in the facets row, right after the Location pill (e.g. the Sort control). */
  trailing?: ReactNode;
  onYearsChange: (years: Array<number | string>) => void;
  onTagsChange: (tags: string[]) => void;
  onLocationsChange: (locations: string[]) => void;
  onSearchChange: (query: string) => void;
  onClear: () => void;
}

export function FilterBar({
  availableYears,
  availableTags,
  availableLocations,
  selectedYears,
  selectedTags,
  selectedLocations,
  search,
  lockedYear,
  hideYear,
  resultCount,
  totalCount,
  trailing,
  onYearsChange,
  onTagsChange,
  onLocationsChange,
  onSearchChange,
  onClear,
}: FilterBarProps) {
  const yearOptions = availableYears.map((y) => ({ value: String(y), display: String(y) }));
  const tagOptions = availableTags.map((t) => ({ value: t, display: t }));
  const locationOptions = availableLocations.map((l) => ({ value: l, display: l }));
  const selectedYearStrings = selectedYears.map(String);

  // The locked year (on the live fair page) is not a user-applied filter, so it doesn't
  // count toward "has active filters" — only years the user added beyond it do.
  const hasYearFilter =
    lockedYear !== undefined
      ? selectedYears.some((y) => String(y) !== String(lockedYear))
      : selectedYears.length > 0;
  const hasActiveFilters =
    hasYearFilter || selectedTags.length > 0 || selectedLocations.length > 0 || search.trim() !== "";

  // Removable summary chips for every applied filter. The pinned live-fair year is
  // excluded — it is not a user-applied filter and cannot be dismissed here.
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  if (search.trim() !== "") {
    chips.push({
      key: "search",
      label: `“${search.trim()}”`,
      onRemove: () => onSearchChange(""),
    });
  }
  selectedYears
    .filter((y) => lockedYear === undefined || String(y) !== String(lockedYear))
    .forEach((y) =>
      chips.push({
        key: `year-${y}`,
        label: String(y),
        onRemove: () => onYearsChange(selectedYears.filter((v) => String(v) !== String(y))),
      })
    );
  selectedTags.forEach((t) =>
    chips.push({
      key: `tag-${t}`,
      label: t,
      onRemove: () => onTagsChange(selectedTags.filter((v) => v !== t)),
    })
  );
  selectedLocations.forEach((l) =>
    chips.push({
      key: `loc-${l}`,
      label: l,
      onRemove: () => onLocationsChange(selectedLocations.filter((v) => v !== l)),
    })
  );

  const showSummary = chips.length > 0 || typeof resultCount === "number";

  return (
    <div className="projects-filter-panel">
      <div className="projects-filter-search-row">
        <span className="projects-search-label" aria-hidden="true">
          Search projects
        </span>
        <PrismInputText
          className="projects-search"
          type="search"
          label="Search projects"
          hideLabel
          name="projects-search"
          placeholder="Search title, description, people, tags…"
          value={search}
          onPrismInput={(e: CustomEvent<{ value?: string }>) =>
            onSearchChange(String(e.detail?.value ?? ""))
          }
          onPrismChange={(e: CustomEvent<{ value?: string }>) =>
            onSearchChange(String(e.detail?.value ?? ""))
          }
        />
      </div>

      <div className="projects-filter-controls">
        <div className="projects-filter-facets">
          {!hideYear && (
            <MultiSelectDropdown
              label="Year"
              options={yearOptions}
              selected={selectedYearStrings}
              disabled={lockedYear !== undefined}
              onChange={onYearsChange}
            />
          )}
          <MultiSelectDropdown
            label="Topic"
            options={tagOptions}
            selected={selectedTags}
            searchable
            onChange={onTagsChange}
          />
          <MultiSelectDropdown
            label="Location"
            options={locationOptions}
            selected={selectedLocations}
            onChange={onLocationsChange}
          />
          {trailing}
        </div>

        <div className="projects-filter-actions">
          <PrismButton
            className="projects-clear-filters"
            tone="secondary"
            display="ghost"
            size="sm"
            disabled={!hasActiveFilters}
            onPrismClick={() => onClear()}
          >
            Clear filters
          </PrismButton>
        </div>
      </div>

      {showSummary && (
        <div className="projects-filter-summary">
          <div className="projects-filter-chips">
            {chips.map((chip) => (
              <PrismButton
                key={chip.key}
                className="projects-filter-chip"
                tone="secondary"
                display="outline"
                size="sm"
                onPrismClick={chip.onRemove}
                aria-label={`Remove filter ${chip.label}`}
              >
                {chip.label}
                <span className="projects-filter-chip-x" aria-hidden="true">
                  ×
                </span>
              </PrismButton>
            ))}
          </div>
          {typeof resultCount === "number" && (
            <span className="projects-filter-count" aria-live="polite">
              Showing {resultCount}
              {typeof totalCount === "number" ? ` of ${totalCount}` : ""} project
              {resultCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
